import { useState, type ReactNode } from "react";

import {
  useCollectionAddGroup,
  useCollectionAddUser,
  useCollectionGroupMembers,
  useCollectionMembers,
  useCollectionRemoveGroup,
  useCollectionRemoveUser,
} from "../api/collections";
import { useGroups, useUsersList } from "../api/queries";
import type { Collection, Permission } from "../api/types";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { useToast } from "../ui/toast-context";

const PERMISSIONS: { value: Permission; label: string }[] = [
  { value: "read", label: "Can read" },
  { value: "read_write", label: "Can edit" },
  { value: "admin", label: "Can manage" },
];

/**
 * Who may reach this collection (`docs/spec/permissions.md` §3): the direct
 * user grants and the group grants, added and removed here. A grant that
 * carries a `sourceId` was inherited from somewhere else and is shown but not
 * editable.
 */
export function CollectionPermissionsDialog({
  open,
  collection,
  onClose,
}: {
  open: boolean;
  collection: Collection;
  onClose: () => void;
}): ReactNode {
  const members = useCollectionMembers(collection.id, open);
  const groupMembers = useCollectionGroupMembers(collection.id, open);
  const users = useUsersList({ limit: 100, offset: 0 }, open);
  const groups = useGroups({});
  const addUser = useCollectionAddUser();
  const removeUser = useCollectionRemoveUser();
  const addGroup = useCollectionAddGroup();
  const removeGroup = useCollectionRemoveGroup();
  const toast = useToast();

  const [userId, setUserId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [permission, setPermission] = useState<Permission>("read_write");

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  const nameOf = (id: string): string =>
    (members.data?.users ?? []).find((user) => user.id === id)?.name ??
    (users.data?.users ?? []).find((user) => user.id === id)?.name ??
    id;

  const groupNameOf = (id: string): string =>
    (groupMembers.data?.groups ?? []).find((group) => group.id === id)?.name ??
    (groups.data?.groups ?? []).find((group) => group.id === id)?.name ??
    id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Collection permissions"
      description={collection.name}
      width="md"
    >
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">People</h3>
        <ul
          aria-label="Collection members"
          className="divide-y divide-line rounded-md border border-line"
        >
          {(members.data?.memberships ?? []).length === 0 ? (
            <li className="px-3 py-3 text-sm text-muted">Nobody has a direct grant.</li>
          ) : (
            (members.data?.memberships ?? []).map((membership) => (
              <li key={membership.id} className="flex items-center justify-between px-3 py-2">
                <span className="text-sm">{nameOf(membership.userId)}</span>
                <span className="flex items-center gap-2 text-xs text-muted">
                  {membership.permission}
                  {membership.sourceId === null ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        removeUser.mutate(
                          { id: collection.id, userId: membership.userId },
                          { onError: report },
                        );
                      }}
                    >
                      Remove
                    </Button>
                  ) : (
                    <span>inherited</span>
                  )}
                </span>
              </li>
            ))
          )}
        </ul>

        <div className="flex items-end gap-2">
          <Field label="Add a person" className="flex-1">
            {(id) => (
              <select
                id={id}
                value={userId}
                onChange={(event) => {
                  setUserId(event.target.value);
                }}
                className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm"
              >
                <option value="">Choose someone…</option>
                {(users.data?.users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Permission">
            {(id) => (
              <select
                id={id}
                value={permission}
                onChange={(event) => {
                  setPermission(event.target.value as Permission);
                }}
                className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
              >
                {PERMISSIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Button
            variant="primary"
            disabled={userId === ""}
            onClick={() => {
              addUser.mutate(
                { id: collection.id, userId, permission },
                {
                  onSuccess: () => {
                    setUserId("");
                  },
                  onError: report,
                },
              );
            }}
          >
            Add
          </Button>
        </div>
      </section>

      <section className="mt-6 flex flex-col gap-2">
        <h3 className="text-sm font-medium">Groups</h3>
        <ul
          aria-label="Collection groups"
          className="divide-y divide-line rounded-md border border-line"
        >
          {(groupMembers.data?.groupMemberships ?? []).length === 0 ? (
            <li className="px-3 py-3 text-sm text-muted">No group has a grant.</li>
          ) : (
            (groupMembers.data?.groupMemberships ?? []).map((membership) => (
              <li key={membership.id} className="flex items-center justify-between px-3 py-2">
                <span className="text-sm">{groupNameOf(membership.groupId)}</span>
                <span className="flex items-center gap-2 text-xs text-muted">
                  {membership.permission}
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      removeGroup.mutate(
                        { id: collection.id, groupId: membership.groupId },
                        { onError: report },
                      );
                    }}
                  >
                    Remove
                  </Button>
                </span>
              </li>
            ))
          )}
        </ul>

        <div className="flex items-end gap-2">
          <Field label="Add a group" className="flex-1">
            {(id) => (
              <select
                id={id}
                value={groupId}
                onChange={(event) => {
                  setGroupId(event.target.value);
                }}
                className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm"
              >
                <option value="">Choose a group…</option>
                {(groups.data?.groups ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Button
            variant="primary"
            disabled={groupId === ""}
            onClick={() => {
              addGroup.mutate(
                { id: collection.id, groupId, permission },
                {
                  onSuccess: () => {
                    setGroupId("");
                  },
                  onError: report,
                },
              );
            }}
          >
            Add
          </Button>
        </div>
      </section>

      <div className="mt-6 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}
